import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import type { GenericField, OId } from '@Types';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import { capitalizeStr } from '@Helpers';
import { useAnimalProfileStore, useSessionStore } from '@Stores';
import { ROUTES } from '@AnimalModule';
import { useForm } from '@FormModule';

interface MedicalStaff {
  [key: string]: any;
  first_name: string;
  last_name: string;
  _id: OId;
}

const currentSchemaModels = ref<Set<GenericField>>(new Set());
const fieldsSchema = ref<FieldSchema[]>([]);
const formError = ref();

export default function () {
  const { t } = useI18n();
  const { getDetailsSchema, wasFormSubmitted } = useForm();
  const { medicationTemplates, medicalStaff } = storeToRefs(useAnimalProfileStore());
  const { shelterProfile } = storeToRefs(useSessionStore());

  const rawFields = ref<RawField[]>([
    {
      label: 'Medication / Treatment',
      name: 'medication',
      required: true,
      type: 'select',
      value: '',
    },
    {
      label: 'Status',
      name: 'status',
      required: true,
      type: 'radio',
      value: '',
      values: [t('common.schedule'), t('common.complete')],
    },
    {
      label: 'Administered By',
      name: 'by',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Other',
      name: 'other_by',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Reason for Treatment',
      name: 'reason',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Start Date',
      name: 'start_date',
      required: true,
      type: 'date',
      value: '',
    },
    {
      label: 'Start Time',
      name: 'time',
      required: false,
      type: 'date',
      value: '',
    },
    {
      label: 'Route',
      name: 'route',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Amount',
      name: 'amount',
      required: false,
      type: 'amount_unit',
      value: '',
    },
    {
      label: '',
      name: 'multi_dose',
      required: false,
      type: 'toggle',
      value: false,
    },
    {
      label: '',
      name: 'separator_frequency',
      required: false,
      type: 'seperator',
      value: '',
    },
    {
      label: 'Frequency',
      name: 'frequency',
      required: false,
      type: 'frequency',
      value: '',
    },
    {
      label: 'Was the initial dose completed?',
      name: 'complete_first_dose',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: 'Initial Dose Administered By',
      name: 'complete_first_dose_by',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Other',
      name: 'other_complete_by',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'First Dose Notes',
      name: 'complete_first_dose_notes',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: '',
      name: 'separator_frequency',
      required: false,
      type: 'seperator',
      value: '',
    },
    {
      label: 'Notes',
      name: 'notes',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: '',
      name: 'schedule_followup',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: '',
      name: 'separator_followup',
      required: false,
      type: 'seperator',
      value: '',
    },
    {
      label: 'Time Until Follow-Up',
      name: 'time_until_followup',
      required: false,
      type: 'timeframe',
      value: '',
    },
    {
      label: 'Follow-Up Reason',
      name: 'followup_reason',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: 'Follow-Up Notes',
      name: 'followup_notes',
      required: false,
      type: 'textarea',
      value: '',
    },
    {
      label: '',
      name: 'separator_followup',
      required: false,
      type: 'seperator',
      value: '',
    },
    {
      label: 'Attending Vet',
      name: 'attending_vet',
      required: false,
      type: 'select',
      value: '',
    },
    {
      label: 'Other Veterinarian',
      name: 'other_vet',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Set Default Costs',
      name: 'track_medical_costs',
      required: false,
      type: 'toggle',
      value: '',
    },
    {
      label: 'Clinic Cost',
      name: 'clinic_cost',
      required: false,
      type: 'cost_unit',
      value: '',
    },
    {
      label: 'Retail Cost',
      name: 'retail_cost',
      required: false,
      type: 'cost_unit',
      value: '',
    },
  ]);

  const attendingVetOptions = computed(() =>
    medicalStaff.value.map((staff: MedicalStaff) => {
      const { first_name, last_name } = staff;
      return {
        ...staff,
        by: `${first_name} ${last_name}`,
      };
    })
  );

  const fieldsSettings = computed(
    (): FieldInitialSettings => ({
      status: {
        settings: {
          hint: 'animals.individualPage.medical.medications.completeMedicationHint',
        },
      },
      by: {
        edit: {
          label: 'by',
          options: [...attendingVetOptions.value, { by: t('common.other') }],
        },
        settings: {
          required: false,
          show: true,
        },
      },
      complete_first_dose_by: {
        edit: {
          label: 'by',
          options: [...attendingVetOptions.value, { by: t('common.other') }],
        },
        settings: {
          required: false,
        },
      },
      attending_vet: {
        edit: {
          label: 'by',
          options: [...attendingVetOptions.value, { by: t('common.other') }],
        },
      },
      medication: {
        edit: {
          label: 'template_name',
          options: medicationTemplates.value.sort((a, b) =>
            a.template_name.localeCompare(b.template_name, undefined, { numeric: true })
          ),
        },
      },
      route: {
        edit: {
          options: Object.values(ROUTES),
        },
      },
      frequency: {
        settings: {
          required: false,
          show: false,
        },
      },
      separator_frequency: {
        settings: {
          show: false,
        },
      },
      followup_notes: {
        settings: {
          required: false,
          show: false,
        },
      },
      time: {
        settings: {
          timeOnly: true,
        },
      },
      separator_followup: {
        settings: {
          show: false,
        },
      },
      time_until_followup: {
        settings: {
          hasCalendar: true,
          required: false,
          show: false,
        },
      },
      multi_dose: {
        model: false,
        settings: {
          label: 'Multiple Doses / Treatments',
        },
      },
      schedule_followup: {
        settings: {
          label: 'Schedule a Follow-Up Check',
        },
      },
      other_vet: {
        settings: {
          show: false,
        },
      },
      other_by: {
        settings: {
          show: false,
        },
      },
      other_complete_by: {
        settings: {
          show: false,
        },
      },
      track_medical_costs: {
        settings: {
          show: !!shelterProfile.value?.track_medical_costs,
        },
      },
      clinic_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
        },
      },
      retail_cost: {
        settings: {
          required: false,
          show: false,
          allowZeroAsValidModel: true,
        },
      },
    })
  );

  const medicationField = computed(() => fieldsSchema.value.find(({ name }) => name === 'medication'));
  const multiDoseField = computed(() => fieldsSchema.value.find(({ name }) => name === 'multi_dose'));
  const followUpField = computed(() => fieldsSchema.value.find(({ name }) => name === 'schedule_followup'));
  const completeFirstDose = computed(() => fieldsSchema.value.find(({ name }) => name === 'complete_first_dose'));
  const completeFirstDoseModel = computed(() => completeFirstDose.value?.model);
  const statusField = computed(() => fieldsSchema.value.find(({ name }) => name === 'status'));
  const isStatus = computed(() => statusField.value?.model);
  const vetField = computed(() => fieldsSchema.value.find(({ name }) => name === 'attending_vet'));
  const vetModel = computed(() => vetField.value?.model);
  const byField = computed(() => fieldsSchema.value.find(({ name }) => name === 'by'));
  const amountField = computed(() => fieldsSchema.value.find(({ name }) => name === 'amount'));
  const frequencyField = computed(() => fieldsSchema.value.find(({ name }) => name === 'frequency'));
  const frequencyModel = computed(() => frequencyField.value?.model);
  const amountModel = computed(() => amountField.value?.model);
  const byModel = computed(() => byField.value?.model);
  const completeByField = computed(() => fieldsSchema.value.find(({ name }) => name === 'complete_first_dose_by'));
  const trackMedicalCostsField = computed(() => fieldsSchema.value.find(({ name }) => name === 'track_medical_costs'));
  const trackMedicalCostsModel = computed(() => trackMedicalCostsField.value?.model);
  const completeByModel = computed(() => completeByField.value?.model);
  const medicationModel = computed(() => medicationField.value?.model);
  const isMultiDose = computed(() => multiDoseField.value?.model);
  const isFollowUp = computed(() => followUpField.value?.model);

  watch(completeFirstDoseModel, () => showCompleteFirstDoseFields());
  watch(isStatus, () => showHiddenFields());
  watch(isMultiDose, () => showFrequencyFields());
  watch(isFollowUp, () => showFollowUpFields());
  watch(medicationModel, (newValue, oldValue) => fillFormWithTemplate(newValue, oldValue));
  watch(vetModel, () => showOtherVetField());
  watch(byModel, () => showOtherByField());
  watch(completeByModel, () => showOtherCompleteByField());
  watch(trackMedicalCostsModel, () => showMedicalCostsFields());
  watch([amountModel, frequencyModel], () => passAmountToMedicalCostFields());

  function passAmountToMedicalCostFields() {
    const totalDoses = frequencyModel?.value?.doses || 1;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.settings.amount = {
        amount: amountModel?.value?.amount,
        amount_unit: amountModel?.value?.amount_unit,
        doses: totalDoses,
      };
    });
  }

  function showMedicalCostsFields() {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      field.settings = {
        ...field.settings,
        show: trackMedicalCostsModel.value,
        required: trackMedicalCostsModel.value,
      };
      field.state?.errors?.clear();
    });
  }

  function showOtherVetField() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'other_vet') {
        const showField = vetModel?.value?.by === t('common.other');

        field.settings = {
          ...field.settings,
          show: showField,
          required: showField,
        };

        if (vetModel?.value?.by !== t('common.other')) return (field.model = '');
      }
    });

    return true;
  }

  function showOtherByField() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'other_by') {
        const showField = byModel?.value?.by === t('common.other');

        field.settings = {
          ...field.settings,
          show: showField,
          required: showField,
        };

        if (byModel?.value?.by !== t('common.other')) {
          field.model = '';
          field.value = '';
          field.state.errors.clear();
        }
      }
    });

    return true;
  }

  function showOtherCompleteByField() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'other_complete_by') {
        const showField = completeByModel?.value?.by === t('common.other');

        field.settings = {
          ...field.settings,
          show: showField,
          required: showField,
        };

        if (completeByModel?.value?.by !== t('common.other')) {
          field.model = '';
          field.value = '';
          field.state.errors.clear();
        }
      }
    });

    return true;
  }

  function fillFormWithTemplate(newValue: any, oldValue: any) {
    if (!newValue || oldValue === newValue) return;

    updateSchemaModels(medicationModel.value);
  }

  function showCompleteFirstDoseFields() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'complete_first_dose_by') {
        field.settings = {
          ...field.settings,
          required: completeFirstDoseModel.value,
          show: completeFirstDoseModel.value,
        };
        field.model = '';
        field.value = '';
        field.state.errors.clear();
      }

      if (field.name === 'complete_first_dose_notes') {
        field.settings = {
          ...field.settings,
          show: completeFirstDoseModel.value,
        };
      }
    });

    return true;
  }

  function showFrequencyFields() {
    fieldsSchema.value.forEach((field) => {
      const frequencyFields = ['frequency', 'separator_frequency', 'time'];

      if (frequencyFields.includes(field.name)) {
        const showField = isMultiDose.value;

        field.settings = {
          ...field.settings,
          required: field.name === 'frequency' ? showField : false,
          show: field.name === 'time' ? !showField : showField,
        };
        field.model = undefined;

        if (!isMultiDose.value) field.state.errors.clear();
      }
    });

    return true;
  }

  function showHiddenFields() {
    fieldsSchema.value.forEach((field) => {
      if (field.name === 'by') {
        const showField = isStatus.value === 'Complete';

        field.settings = {
          ...field.settings,
          required: showField,
          show: showField,
        };
        field.model = '';
        field.value = '';
        field.state.errors.clear();
      }
      if (field.name === 'complete_first_dose') {
        const showField = isStatus.value === 'Schedule';

        field.settings = {
          ...field.settings,
          show: showField,
        };
        field.model = !showField ? false : field.model;
        field.value = !showField ? false : field.model;
      }
      if (field.name === 'complete_first_dose_by') {
        const showField = isStatus.value === 'Schedule';

        field.model = !showField ? null : field.model;
        field.value = !showField ? null : field.model;
      }
      if (field.name === 'complete_first_dose_notes') {
        const showField = isStatus.value === 'Schedule';

        field.model = !showField ? '' : field.model;
        field.value = !showField ? '' : field.model;
      }
    });

    return true;
  }

  function showFollowUpFields() {
    fieldsSchema.value.forEach((field) => {
      const followUpFields = ['followup_notes', 'followup_reason', 'separator_followup', 'time_until_followup'];

      const nonRequiredFields = ['followup_notes', 'followup_reason', 'separator_followup'];

      if (followUpFields.includes(field.name)) {
        const showField = isFollowUp.value === true;

        field.settings = {
          ...field.settings,
          required: nonRequiredFields.includes(field.name) ? false : showField,
          show: showField,
        };
      }

      if (!isFollowUp.value) field.state.errors.clear();
    });

    return true;
  }

  function setForm() {
    fieldsSchema.value = getDetailsSchema(rawFields.value, fieldsSettings.value);

    if (!medicationModel.value) {
      fieldsSchema.value.forEach((field: any) => {
        if (field.name === 'medication') return;

        field.state.disabled = true;
        field.settings.disabled = true;
      });
    }
  }

  function updateSchemaModels(sourceData: any) {
    formError.value = null;
    wasFormSubmitted.value = false;

    const toggleableFields = [
      'frequency',
      'separator_frequency',
      'followup_notes',
      'followup_reason',
      'time_until_followup',
      'clinic_cost',
      'retail_cost',
    ];

    fieldsSchema.value.forEach((field) => {
      const match = sourceData[field.name];
      if (toggleableFields.includes(field.name)) return;

      switch (field.name) {
        case 'status': {
          setMatchStatusField(field, sourceData);
          break;
        }
        case 'reason': {
          setMatchField(field, match);
          break;
        }
        case 'attending_vet': {
          setByField(field, sourceData);
          break;
        }
        case 'route': {
          setMatchField(field, match);
          break;
        }
        case 'notes': {
          setMatchField(field, match);
          break;
        }
        case 'start_date': {
          setDateField(field, match);
          break;
        }
        case 'amount': {
          setAmountField(field, sourceData);
          break;
        }
        case 'track_medical_costs': {
          setMatchField(field, match || false);
          showMedicalCostsFields();
          nextTick(() => {
            fillMedicalCostsFields(sourceData);
          });
          break;
        }
        case 'multi_dose': {
          setMatchField(field, match);
          showFrequencyFields();
          nextTick(() => {
            fillFrequencyFields(sourceData);
          });
          break;
        }
        case 'schedule_followup': {
          setMatchField(field, match);
          showFollowUpFields();
          nextTick(() => {
            fillFollowUpFields(sourceData);
          });
          break;
        }
      }
    });
  }

  function setMatchField(field: any, match: any) {
    field.model = match;
    field.value = match;
    field.state.errors.clear();
  }

  function setMatchStatusField(field: any, sourceData: any) {
    field.model = sourceData.status === 'active' ? 'Schedule' : 'Complete';
    field.value = sourceData.status === 'active' ? 'Schedule' : 'Complete';
    field.state.errors.clear();
  }

  function setDateField(field: any, match: any) {
    field.model = match || new Date();
    field.value = match || new Date();
  }

  function setAmountField(field: any, sourceData: any) {
    field.model = { amount: sourceData.amount, amount_unit: sourceData.amount_unit };
    field.value = { amount: sourceData.amount, amount_unit: sourceData.amount_unit };
  }

  function fillMedicalCostsFields(sourceData: any) {
    if (!shelterProfile.value?.track_medical_costs) return;

    fieldsSchema.value.forEach((field) => {
      const medicalCostFields = ['clinic_cost', 'retail_cost'];
      if (!medicalCostFields.includes(field.name)) return;

      const costValue = sourceData.medical_costs_settings?.[field.name];
      const costType = sourceData.medical_costs_settings?.[`${field.name}_type`];

      if (costValue !== undefined && costValue !== null && costType) {
        field.model = {
          value: Number(costValue),
          type: costType,
        };
        field.value = {
          value: Number(costValue),
          type: costType,
        };
      }
      field.state.errors.clear();
    });
  }

  function setByField(field: any, sourceData: any) {
    if (!sourceData.by) return;

    const fullStaffData = medicalStaff.value.find(
      (staff: MedicalStaff) => staff._id?.$oid === (sourceData.by_id?.$oid || sourceData.by_id)
    );
    if (!fullStaffData) return;

    const { first_name, last_name, _id } = fullStaffData;
    const by = { _id, first_name, last_name, by: sourceData.by };

    field.model = by;
    field.value = by;
  }

  function setFrequencyField(field: any, sourceData: any) {
    const duration = sourceData.duration || '0';
    field.model = {
      frequency: sourceData.frequency,
      duration: duration,
      duration_unit: sourceData.duration_unit,
      time1: sourceData.time1 || new Date(),
      time2: sourceData.time2 || null,
      time3: sourceData.time3 || null,
      time4: sourceData.time4 || null,
      initial_dose_index: sourceData.initial_dose_index || 0,
    };
    field.value = {
      frequency: sourceData.frequency,
      duration: duration,
      duration_unit: capitalizeStr(sourceData.duration_unit),
      time1: sourceData.time1 || new Date(),
      time2: sourceData.time2 || null,
      time3: sourceData.time3 || null,
      time4: sourceData.time4 || null,
      initial_dose_index: sourceData.initial_dose_index || 0,
    };
  }

  function setTimeUntilFollowUpField(field: any, sourceData: any) {
    if (!sourceData.time_until_followup || !sourceData.followup_unit) return;

    field.model = {
      duration: sourceData.time_until_followup,
      duration_unit: sourceData.followup_unit,
    };
    field.value = {
      duration: sourceData.time_until_followup,
      duration_unit: sourceData.followup_unit,
    };
  }

  function fillFrequencyFields(sourceData: any) {
    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'frequency') return;

      field.state.errors.clear();
      setFrequencyField(field, sourceData);
    });
  }

  function fillFollowUpFields(sourceData: any) {
    fieldsSchema.value.forEach((field) => {
      const followUpFields = ['followup_notes', 'followup_reason', 'time_until_followup'];
      const match = sourceData[field.name];

      field.state.disabled = false;
      field.settings.disabled = false;

      if (!followUpFields.includes(field.name)) return;
      field.state.errors.clear();
      // prettier-ignore
      switch (field.name) {
        case 'followup_notes': return setMatchField(field, match);
        case 'followup_reason': return setMatchField(field, match);
        case 'time_until_followup': return setTimeUntilFollowUpField(field, sourceData);
      }
    });
  }

  function resetFieldSchema() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
    formError.value = null;
    wasFormSubmitted.value = false;
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    fieldsSettings,
    formError,
    resetFieldSchema,
    setForm,
  };
}
